import { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Upload, Newspaper } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["National", "International", "Politics", "Business", "Technology", "Sports", "Entertainment", "Lifestyle", "Opinion", "Video", "Photo", "District"];

export default function NewsManager({ websiteId }) {
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [epaperOpen, setEpaperOpen] = useState(false);
  const [epapers, setEpapers] = useState([]);

  const load = () => api.get(`/articles?website_id=${websiteId}`).then(r => setArticles(r.data));
  const loadEp = () => api.get(`/epapers?website_id=${websiteId}`).then(r => setEpapers(r.data));
  useEffect(() => { load(); loadEp(); }, [websiteId]);

  const del = async (id) => {
    if (!confirm("Delete article?")) return;
    await api.delete(`/articles/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-bold">News Articles ({articles.length})</h3>
        <div className="flex gap-2">
          <Button onClick={() => setEpaperOpen(true)} variant="outline" className="rounded-full" data-testid="add-epaper"><Newspaper className="w-4 h-4 mr-1.5"/>E-Paper</Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-rose-500 hover:bg-rose-600 rounded-full" data-testid="add-article">
            <Plus className="w-4 h-4 mr-1.5"/>New Article
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {articles.length === 0 && <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed">No articles yet</div>}
        {articles.map(a => (
          <div key={a.article_id} className="p-4 bg-white rounded-xl border border-slate-200 flex gap-3" data-testid={`article-${a.article_id}`}>
            <img src={fileUrl(a.featured_image) || "https://placehold.co/120x80?text=News"} className="w-24 h-16 rounded-lg object-cover border" alt=""/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="uppercase text-[10px]">{a.category}</Badge>
                <Badge className={a.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{a.status}</Badge>
                <span className="text-xs text-slate-500">👁 {a.views}</span>
              </div>
              <div className="font-bold truncate mt-1">{a.title}</div>
              <div className="text-xs text-slate-500 truncate">{a.excerpt}</div>
            </div>
            <div className="flex flex-col gap-1">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(a); setOpen(true); }}><Edit className="w-3 h-3"/></Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600" onClick={() => del(a.article_id)}><Trash2 className="w-3 h-3"/></Button>
            </div>
          </div>
        ))}
      </div>

      {epapers.length > 0 && (
        <div className="mt-8">
          <h4 className="font-bold mb-3">E-Paper Editions</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {epapers.map(ep => (
              <div key={ep.epaper_id} className="bg-white rounded-lg border overflow-hidden">
                <div className="aspect-[3/4] bg-slate-100 flex items-center justify-center">
                  {ep.pages?.[0] ? <img src={fileUrl(ep.pages[0])} alt="" className="w-full h-full object-cover"/> : <Newspaper className="w-12 h-12 text-slate-300"/>}
                </div>
                <div className="p-2">
                  <div className="text-xs font-bold truncate">{ep.title}</div>
                  <div className="text-[10px] text-slate-500">{ep.edition_date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ArticleDialog open={open} onOpenChange={setOpen} websiteId={websiteId} editing={editing} onSaved={() => { load(); setOpen(false); }}/>
      <EPaperDialog open={epaperOpen} onOpenChange={setEpaperOpen} websiteId={websiteId} onSaved={() => { loadEp(); setEpaperOpen(false); }}/>
    </div>
  );
}

function ArticleDialog({ open, onOpenChange, websiteId, editing, onSaved }) {
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", category: "National", featured_image: "", author: "", status: "draft" });
  const fileRef = useRef();
  useEffect(() => {
    setForm(editing || { title: "", excerpt: "", body: "", category: "National", featured_image: "", author: "", status: "draft" });
  }, [editing, open]);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm(f => ({ ...f, featured_image: r.data.path }));
  };

  const aiHeadline = async () => {
    try {
      const r = await api.post("/ai/generate", { task: "headline", prompt: form.body || form.title || "news article", context: `Category: ${form.category}` });
      setForm({ ...form, title: r.data.output.split("\n")[0].replace(/^["*-]+|["*]+$/g, "").trim() });
      toast.success("Headline generated");
    } catch { toast.error("AI failed"); }
  };

  const submit = async () => {
    if (!form.title || !form.body) return toast.error("Title & body required");
    try {
      if (editing) await api.put(`/articles/${editing.article_id}`, form);
      else await api.post("/articles", { ...form, website_id: websiteId });
      toast.success("Saved"); onSaved();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Article</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
          <div>
            <div className="flex justify-between items-center">
              <Label>Headline</Label>
              <Button type="button" size="sm" variant="ghost" onClick={aiHeadline} className="text-xs text-sky-600" data-testid="ai-headline">✨ AI Headline</Button>
            </div>
            <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="art-title"/>
          </div>
          <div><Label>Excerpt</Label><Textarea value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} rows={2}/></div>
          <div><Label>Body</Label><Textarea value={form.body} onChange={e => setForm({...form, body: e.target.value})} rows={8} data-testid="art-body"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
          </div>
          <div><Label>Author</Label><Input value={form.author} onChange={e => setForm({...form, author: e.target.value})}/></div>
          <div>
            <Label>Featured image</Label>
            <input ref={fileRef} type="file" hidden onChange={e => upload(e.target.files[0])}/>
            <div className="mt-1 flex items-center gap-2">
              {form.featured_image && <img src={fileUrl(form.featured_image)} className="w-20 h-14 rounded-lg object-cover border" alt=""/>}
              <Button type="button" onClick={() => fileRef.current?.click()} variant="outline"><Upload className="w-4 h-4 mr-1.5"/>Upload</Button>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-rose-500 hover:bg-rose-600" data-testid="art-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EPaperDialog({ open, onOpenChange, websiteId, onSaved }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [pages, setPages] = useState([]);
  const fileRef = useRef();

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPages(p => [...p, r.data.path]);
    toast.success("Page uploaded");
  };

  const submit = async () => {
    if (!title || pages.length === 0) return toast.error("Title & at least 1 page required");
    await api.post("/epapers", { website_id: websiteId, title, edition_date: date, pages });
    toast.success("E-paper created"); onSaved();
    setTitle(""); setPages([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Upload E-Paper Edition</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Edition title</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="ঢাকা এডিশন — ২৪ ফেব্রুয়ারি"/></div>
          <div><Label>Edition date</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)}/></div>
          <div>
            <Label>Pages ({pages.length})</Label>
            <input ref={fileRef} type="file" hidden accept="image/*" onChange={e => upload(e.target.files[0])}/>
            <Button type="button" onClick={() => fileRef.current?.click()} variant="outline" className="mt-1"><Upload className="w-4 h-4 mr-1.5"/>Add page image</Button>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {pages.map((p, i) => <img key={i} src={fileUrl(p)} className="aspect-[3/4] rounded object-cover border" alt=""/>)}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-rose-500 hover:bg-rose-600">Publish edition</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
